/**
 * "Can the rest alert wake the screen?", in Settings.
 *
 * The alert itself is a full-screen intent posted by the RestAlarm native
 * plugin, and on Android 14+ the system decides whether an app may use one.
 * Calling and alarm apps are granted it at install; everyone else is denied
 * until the user finds Special app access > Full screen intents, which nobody
 * finds. Denied, the failure is invisible in the worst way this app has form
 * for: the notification still arrives, on time, buzzing — onto a dark screen.
 *
 * So it is stated rather than assumed. The permission is checked fresh every
 * time this renders and again when the app comes back to the foreground, which
 * is what makes the trip to Settings feel like it worked: you flip the toggle,
 * swipe back, and the line has already changed.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  openRestAlertSettings,
  restAlertStatus,
  type RestAlertStatus,
} from '../native/notify'

export function RestWake() {
  // undefined = still asking, null = not applicable (web, or the call failed).
  const [status, setStatus] = useState<RestAlertStatus | null | undefined>(undefined)

  const refresh = useCallback(() => {
    let cancelled = false
    void restAlertStatus().then((s) => { if (!cancelled) setStatus(s) })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const stop = refresh()
    // The user leaves for the Settings app and comes back. Without this the
    // screen still says "can't wake" after they have just fixed it, which
    // reads as the toggle not having worked.
    const onVisible = () => { if (!document.hidden) refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh])

  if (!status) return null

  if (status.allowed) {
    return (
      <p className="muted" style={{ fontSize: '.85rem', margin: '.6rem 0 0' }}>
        When your rest is up the phone wakes and shows it, even face down in a
        bag with the screen off.
      </p>
    )
  }

  return (
    <div
      style={{
        marginTop: '.7rem',
        padding: '.6rem .7rem',
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius)',
        fontSize: '.88rem',
      }}
    >
      <p style={{ margin: 0 }}>
        Android isn't letting the rest alert wake your screen. It will still
        arrive on time and buzz — you just won't see it until you pick the phone
        up.
      </p>
      <button
        className="secondary"
        style={{ marginTop: '.5rem', padding: '.3rem .7rem', minHeight: 0,
                 fontSize: '.85rem' }}
        onClick={() => { void openRestAlertSettings() }}
      >
        Allow it to wake the screen
      </button>
    </div>
  )
}
